import React, { useState, Fragment } from "react";
import { ToastProvider } from "./components/shared/ToastProvider";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import Dashboard from "./pages/Dashboard";
import JobManagement from "./pages/JobManagement";
import CandidatePipeline from "./pages/CandidatePipeline";
import Candidates from "./pages/Candidates";
import AISourcing from "./pages/AISourcing";
import Configuration from "./pages/Configuration";
import Integrations from "./pages/Integrations";
import OpenRequests from "./pages/OpenRequests";
import WorkflowBuilder from "./pages/WorkflowBuilder";
import Communication from "./pages/Communication";
import InterviewScheduling from "./pages/InterviewScheduling";
import CandidateScheduling from "./pages/CandidateScheduling";
import CandidatePortal from "./pages/CandidatePortal";
import CustomerManagement from "./pages/CustomerManagement";
import VendorManagement from "./pages/VendorManagement";
import ContactManagement from "./pages/ContactManagement";
import PersonaManagement from "./pages/PersonaManagement";
import UserManagement from "./pages/UserManagement";
import RecruiterRileyDashboard from "./pages/RecruiterRileyDashboard";
import LeadLenaDashboard from "./pages/LeadLenaDashboard";
import SalesSamDashboard from "./pages/SalesSamDashboard";
import HRHankDashboard from "./pages/HRHankDashboard";
import AdminAndrewDashboard from "./pages/AdminAndrewDashboard";
import LeadSalesManagerDashboard from "./pages/LeadSalesManagerDashboard";
import RecruiterWorkspace from "./pages/RecruiterWorkspace";
import CandidateSubmissions from "./pages/CandidateSubmissions";
import JobView from "./pages/JobView";
import CandidateDashboard from "./pages/CandidateDashboard";
import Submissions from "./pages/Submissions";
import ProtectedRoute from "./components/ProtectedRoute";
import { 
  Briefcase, Users, LayoutDashboard, Menu, UserCircle, Brain, 
  Settings, Link as LinkIcon, ChevronLeft, ChevronRight, FileText, GitBranch,
  MessageCircle, Calendar, Building2, Zap, Send, ChevronDown
} from "lucide-react";

const TopNavigation = () => {
  const [showPersonaMenu, setShowPersonaMenu] = useState(false);
  const location = useLocation();

  const personas = [
    { path: "/", label: "All Personas", icon: "👥", description: "View all personas" },
    { path: "/recruiter-riley", label: "Recruiter Riley", icon: "👤", description: "Recruiter Dashboard", color: "bg-blue-100 text-blue-800" },
    { path: "/lead-lena", label: "Lead Lena", icon: "👑", description: "Lead Dashboard", color: "bg-yellow-100 text-yellow-800" },
    { path: "/sales-sam", label: "Sales Sam", icon: "📈", description: "Sales Dashboard", color: "bg-red-100 text-red-800" },
    { path: "/hr-hank", label: "HR Hank", icon: "💼", description: "HR Dashboard", color: "bg-indigo-100 text-indigo-800" },
    { path: "/admin-andrew", label: "Admin Andrew", icon: "🛡️", description: "Admin Dashboard", color: "bg-purple-100 text-purple-800" },
    { path: "/lead-sales-sarah", label: "Lead Sales Manager Sarah", icon: "📊", description: "Sales Manager Dashboard", color: "bg-pink-100 text-pink-800" }
  ];

  // Find current persona based on path
  const currentPersona = personas.find(p => p.path === location.pathname) || personas[0];

  return (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg relative z-50">
      <div className="px-4">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg p-2">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Juno Talent Assist</h1>
              <p className="text-xs text-blue-200">AI-Powered Talent Platform</p>
            </div>
          </div>

          {/* Persona Selector */}
          <div className="relative">
            <button
              onClick={() => setShowPersonaMenu(!showPersonaMenu)}
              className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
            >
              <span className="text-2xl">{currentPersona.icon}</span>
              <div className="text-left">
                <p className="text-sm font-semibold">{currentPersona.label}</p>
                <p className="text-xs text-blue-200">{currentPersona.description}</p>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform ${showPersonaMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {showPersonaMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowPersonaMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl overflow-hidden z-50">
                  {personas.map((persona) => (
                    <Link
                      key={persona.path}
                      to={persona.path}
                      onClick={() => setShowPersonaMenu(false)}
                      className={`flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        location.pathname === persona.path ? 'bg-blue-50' : ''
                      }`}
                    >
                      <span className="text-2xl">{persona.icon}</span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{persona.label}</p>
                        <p className="text-xs text-gray-500">{persona.description}</p>
                      </div>
                      {location.pathname === persona.path && (
                        <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                      )}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// LeftSidebar component removed - navigation moved to top bar
// const LeftSidebar = () => { ... };

const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <TopNavigation />
      
      {/* Main Content Area - Full Width */}
      <main className="p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <ToastProvider>
        <BrowserRouter>
        <Routes>
          {/* Candidate Portal - Standalone Layout */}
          <Route path="/candidate/:candidateId" element={<CandidatePortal />} />
          <Route path="/schedule/:token" element={<CandidateScheduling />} />
          
          {/* Main Application Routes - With Main Layout */}
          <Route path="/" element={<MainLayout><PersonaManagement /></MainLayout>} />
          <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
          
          {/* Job Management - All roles can view */}
          <Route path="/jobs" element={<MainLayout><JobManagement /></MainLayout>} />
          <Route path="/jobs/:jobId" element={<MainLayout><JobView /></MainLayout>} />
          
          {/* Candidate Management - All roles can view */}
          <Route path="/candidates" element={<MainLayout><CandidateDashboard /></MainLayout>} />
          <Route path="/candidate-dashboard" element={<MainLayout><CandidateDashboard /></MainLayout>} />
          
          {/* AI Sourcing - All roles */}
          <Route path="/ai-sourcing" element={<MainLayout><AISourcing /></MainLayout>} />
          
          {/* Pipeline - All roles */}
          <Route path="/pipeline" element={<MainLayout><CandidatePipeline /></MainLayout>} />
          
          {/* Vendor & Contact Management */}
          <Route path="/companies" element={<MainLayout><VendorManagement /></MainLayout>} />
          <Route path="/contacts" element={<MainLayout><ContactManagement /></MainLayout>} />
          
          {/* Customer Management - All roles */}
          <Route path="/customers" element={<MainLayout><CustomerManagement /></MainLayout>} />
          
          {/* Persona Management - Admin only (keep protection) */}
          <Route path="/personas" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout><PersonaManagement /></MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/persona-management" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout><PersonaManagement /></MainLayout>
            </ProtectedRoute>
          } />
          
          {/* User Management - All roles */}
          <Route path="/users" element={<MainLayout><UserManagement /></MainLayout>} />
          
          {/* Recruiter Workspace - All roles */}
          <Route path="/recruiter-workspace" element={<MainLayout><RecruiterWorkspace /></MainLayout>} />
          
          {/* Persona Dashboards - No protection needed (no authentication system yet) */}
          <Route path="/recruiter-riley" element={<MainLayout><RecruiterRileyDashboard /></MainLayout>} />
          <Route path="/lead-lena" element={<MainLayout><LeadLenaDashboard /></MainLayout>} />
          <Route path="/sales-sam" element={<MainLayout><SalesSamDashboard /></MainLayout>} />
          <Route path="/hr-hank" element={<MainLayout><HRHankDashboard /></MainLayout>} />
          <Route path="/admin-andrew" element={<MainLayout><AdminAndrewDashboard /></MainLayout>} />
          <Route path="/lead-sales-sarah" element={<MainLayout><LeadSalesManagerDashboard /></MainLayout>} />
          
          {/* Submissions - All roles can view */}
          <Route path="/candidate-submissions" element={<MainLayout><CandidateSubmissions /></MainLayout>} />
          <Route path="/submissions" element={<MainLayout><Submissions /></MainLayout>} />
          
          {/* Communication & Interviews - All roles */}
          <Route path="/communication" element={<MainLayout><Communication /></MainLayout>} />
          <Route path="/interviews" element={<MainLayout><InterviewScheduling /></MainLayout>} />
          
          {/* Open Requests - All roles */}
          <Route path="/open-requests" element={<MainLayout><OpenRequests /></MainLayout>} />
          
          {/* Workflow Builder - Admin only (keep protection) */}
          <Route path="/workflow-builder" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout><WorkflowBuilder /></MainLayout>
            </ProtectedRoute>
          } />
          
          {/* Integrations & Configuration - Admin only (keep protection) */}
          <Route path="/integrations" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout><Integrations /></MainLayout>
            </ProtectedRoute>
          } />
          <Route path="/configuration" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout><Configuration /></MainLayout>
            </ProtectedRoute>
          } />
        </Routes>
        
        {/* Toast Notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
        </BrowserRouter>
      </ToastProvider>
    </div>
  );
}

export default App;